<template>
  <div ref="wrapperRef" class="m-stringrange-wrapper">
    <div class="m-stringrange-container">
      <!-- Relation type selector -->
      <Select
        appendTo="#ui-popup-root"
        v-model="selectedRelation"
        :options="relationTypes"
        :disabled="disabled || readonly"
        class="m-stringrange-relation"
        @blur="handleBlur"
      />

      <!-- First value -->
      <InputText
        v-model="firstValue"
        :disabled="disabled || readonly"
        :placeholder="fromPlaceholder"
        class="m-stringrange-input"
        @blur="handleBlur"
      />

      <!-- Middle label (shown only for "between" relation) -->
      <span v-if="showMiddleLabel" class="m-stringrange-separator">
        {{ separatorText }}
      </span>

      <!-- Second value (shown only for "between" relation) -->
      <InputText
        v-if="showSecondInput"
        v-model="secondValue"
        :disabled="disabled || readonly"
        :placeholder="toPlaceholder"
        class="m-stringrange-input"
        @blur="handleBlur"
      />
    </div>

    <div v-if="hint" class="m-stringrange-hint">
      {{ hint }}
    </div>

    <FieldVerifyErrorOverlay :messages="normalizedErrors" :anchor-el="wrapperRef" />
  </div>
</template>

<script setup lang="ts">
import InputText from 'primevue/inputtext'
import Select from 'primevue/select'
import FieldVerifyErrorOverlay from '@/components/ui/FieldVerifyErrorOverlay.vue'
import { computed, ref, watch } from 'vue'

interface RangeValue {
  relation?: string
  first?: string
  second?: string
}

interface Props {
  modelValue?: RangeValue | null
  fromPlaceholder?: string
  toPlaceholder?: string
  separatorText?: string
  hint?: string
  disabled?: boolean
  readonly?: boolean
  errorMessages?: string | string[]
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: null,
  fromPlaceholder: 'От',
  toPlaceholder: 'До',
  separatorText: 'и',
  hint: '',
  disabled: false,
  readonly: false,
  errorMessages: () => []
})

const emit = defineEmits<{
  'update:modelValue': [value: RangeValue]
  'change': [value: RangeValue]
  'blur': []
}>()

const wrapperRef = ref<HTMLElement | null>(null)

// Relation types (like Swing client)
const relationTypes = ['=', '>', '<', '>=', '<=', 'между']

const selectedRelation = ref(props.modelValue?.relation || '=')
const firstValue = ref(props.modelValue?.first || '')
const secondValue = ref(props.modelValue?.second || '')

// Show middle label and second input only for "между" (between)
const showMiddleLabel = computed(() => selectedRelation.value === 'между')
const showSecondInput = computed(() => selectedRelation.value === 'между')

// Sync with model value
watch(() => props.modelValue, (newValue) => {
  if (newValue) {
    selectedRelation.value = newValue.relation || '='
    firstValue.value = newValue.first || ''
    secondValue.value = newValue.second || ''
  }
}, { deep: true })

// Emit changes
watch([selectedRelation, firstValue, secondValue], () => {
  const value: RangeValue = {
    relation: selectedRelation.value,
    first: firstValue.value,
    second: secondValue.value
  }
  emit('update:modelValue', value)
  emit('change', value)
})

const normalizedErrors = computed(() => {
  if (Array.isArray(props.errorMessages)) return props.errorMessages
  return [props.errorMessages]
})

const handleBlur = () => {
  emit('blur')
}
</script>

<style scoped lang="scss">
.m-stringrange-wrapper {
  width: 100%;
}

.m-stringrange-container {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
}

.m-stringrange-relation {
  width: 100px;
  flex-shrink: 0;
}

.m-stringrange-input {
  flex: 1;
  min-width: 0;
}

.m-stringrange-separator {
  color: #6b7280;
  font-size: 0.875rem;
  padding: 0 0.25rem;
  flex-shrink: 0;
}

.m-stringrange-hint {
  font-size: 0.75rem;
  color: #6b7280;
  margin-top: 0.5rem;
}
</style>

